import { Button, Card, message, Segmented, Space, Tag, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { AIModelFrameworkMap, LinkStatus } from '@common';
import type { AppProject } from '@common';
import ApiKeyManagement from './ApiKeyManagement';
import ApiUsageCard from './ApiUsageCard';
import useAppname from './useAppname';
import { useState } from 'react';

const { Title, Text } = Typography;

interface Props {
    app: AppProject;
    linkStatus?: number;
}

type SnippetLang = 'python' | 'curl' | 'javascript' | 'python-requests';

export default function LlmAppContent({ app, linkStatus }: Props) {
    const { appName, maindomain } = useAppname();
    const [snippetLang, setSnippetLang] = useState<SnippetLang>('python');
    const baseUrl = `https://${appName}-proxy.${maindomain}`;
    const isOnline = linkStatus === LinkStatus.ONLINE;
    const framework = app?.aiModelProperties?.framework;
    const frameworkLabel = AIModelFrameworkMap[framework] ?? framework;

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        message.success('Copied to clipboard');
    };

    const snippets = getSnippets(baseUrl);
    const currentSnippet = snippets[snippetLang];

    return (
        <Space direction="vertical" style={{ width: '100%', padding: '24px', maxWidth: 800 }} size="large">
            {/* Status & Base URL */}
            <Card>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                            <Title level={4} style={{ margin: 0 }}>
                                {frameworkLabel}
                            </Title>
                            <Tag color="purple">LLM</Tag>
                        </Space>
                        <Tag
                            icon={isOnline ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            color={isOnline ? 'success' : 'default'}
                        >
                            {isOnline ? 'Online' : 'Offline'}
                        </Tag>
                    </div>
                    <Space>
                        <Text type="secondary">Base URL:</Text>
                        <Text code copyable>{baseUrl}</Text>
                    </Space>
                    <Space>
                        <Text type="secondary">OpenAI-compatible:</Text>
                        <Text code copyable>{baseUrl}/v1</Text>
                    </Space>
                </Space>
            </Card>

            {/* API Key Management */}
            <ApiKeyManagement appName={appName} />

            {/* API Usage */}
            <ApiUsageCard appName={appName} />

            {/* SDK Snippets */}
            <Card
                title="Quick Start"
                extra={
                    <Segmented
                        size="small"
                        value={snippetLang}
                        onChange={v => setSnippetLang(v as SnippetLang)}
                        options={[
                            { label: 'Python', value: 'python' },
                            { label: 'cURL', value: 'curl' },
                            { label: 'JavaScript', value: 'javascript' },
                            { label: 'Requests', value: 'python-requests' },
                        ]}
                    />
                }
            >
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                        <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(currentSnippet)}>
                            Copy
                        </Button>
                    </div>
                    <pre style={{
                        background: '#f5f5f5',
                        padding: 12,
                        borderRadius: 6,
                        fontSize: 13,
                        overflow: 'auto',
                        margin: 0,
                    }}>
                        {currentSnippet}
                    </pre>
                </div>
            </Card>
        </Space>
    );
}

function getSnippets(baseUrl: string): Record<SnippetLang, string> {
    return {
        python: `from openai import OpenAI

client = OpenAI(
    base_url="${baseUrl}/v1",
    api_key="YOUR_API_KEY",  # mpk_... key
)

response = client.chat.completions.create(
    model="llama3.2",
    messages=[{"role": "user", "content": "Hello!"}],
    stream=True,
)

for chunk in response:
    if chunk.choices[0].delta.content:
        print(chunk.choices[0].delta.content, end="")`,

        curl: `curl -N "${baseUrl}/v1/chat/completions" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "model": "llama3.2",
    "messages": [{"role": "user", "content": "Hello!"}],
    "stream": true
  }'`,

        javascript: `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "${baseUrl}/v1",
  apiKey: "YOUR_API_KEY",  // mpk_... key
});

const stream = await client.chat.completions.create({
  model: "llama3.2",
  messages: [{ role: "user", content: "Hello!" }],
  stream: true,
});

for await (const chunk of stream) {
  process.stdout.write(chunk.choices[0]?.delta?.content || "");
}`,

        'python-requests': `import requests

response = requests.post(
    "${baseUrl}/v1/chat/completions",
    headers={
        "Content-Type": "application/json",
        "Authorization": "Bearer YOUR_API_KEY",
    },
    json={
        "model": "llama3.2",
        "messages": [{"role": "user", "content": "Hello!"}],
        "stream": True,
    },
    stream=True,
)

for line in response.iter_lines():
    if line:
        print(line.decode())`,
    };
}
